import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

export const ModalBackdrop = styled.div``;

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 48rem;
  background-color: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #F3F4F6;
  flex-shrink: 0;
`;

export const ModalHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const ModalIconWrapper = styled.div`
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.025em;
`;

export const ModalSubtitle = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  margin-top: 0.25rem;
  line-height: 1.4;
`;

export const ModalCloseButton = styled.button`
  padding: 0.5rem;
  border-radius: 9999px;
  border: none;
  background-color: transparent;
  color: #9CA3AF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: #F3F4F6;
    color: #4B5563;
  }
`;

export const ModalBody = styled.div`
  overflow-y: auto;
  padding: 1.5rem 2rem;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #CBD5E1;
    border-radius: 20px;
  }
`;

export const ModalFooter = styled.div`
  padding: 1.25rem 1.5rem;
  background-color: rgba(249, 250, 251, 0.5);
  border-top: 1px solid #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  border-radius: 0 0 1rem 1rem;
`;

export const ModalFormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

export const ModalLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const ModalInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .input-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9CA3AF;
    pointer-events: none;
    transition: color 0.2s;
  }

  &:focus-within .input-icon {
    color: #6B7280;
  }
`;

export const ModalInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  background-color: #F9FAFB;
  border: 1px solid ${(props) => (props.$hasError ? "#D4443A" : "#E5E7EB")};
  border-radius: 0.75rem;
  font-size: 1rem;
  color: #111827;
  transition: all 0.2s;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? "#D4443A" : "#A8FBCA")};
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(168, 251, 202, 0.1);
  }
`;

export const ModalSelect = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  background-color: #F9FAFB;
  border: 1px solid ${(props) => (props.$hasError ? "#D4443A" : "#E5E7EB")};
  border-radius: 0.75rem;
  font-size: 1rem;
  color: #111827;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? "#D4443A" : "#A8FBCA")};
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(168, 251, 202, 0.1);
  }
`;

export const ModalTextarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 1rem;
  background-color: #F9FAFB;
  border: 1px solid ${(props) => (props.$hasError ? "#D4443A" : "#E5E7EB")};
  border-radius: 0.75rem;
  font-size: 1rem;
  color: #111827;
  font-family: inherit;
  resize: vertical;
  min-height: 5rem;
  transition: all 0.2s;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? "#D4443A" : "#A8FBCA")};
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(168, 251, 202, 0.1);
  }
`;

export const ModalCheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
`;

export const ModalCheckbox = styled.input.attrs({ type: "checkbox" })`
  width: 1.125rem;
  height: 1.125rem;
  margin-top: 0.125rem;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: #3B82F6;
`;

export const ModalCheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #111827;
  cursor: pointer;
  line-height: 1.5;

  a {
    color: #3B82F6;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #2563EB;
      text-decoration: underline;
    }
  }
`;

export const ModalErrorText = styled.span`
  font-size: 0.75rem;
  color: #D4443A;
  margin-top: 0.25rem;
`;

export const ModalCancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  background-color: #ffffff;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #F9FAFB;
    border-color: #D1D5DB;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ModalSubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  background-color: #A8FBCA;
  color: #000000;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(168, 251, 202, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background-color: #8FF5C0;
    box-shadow: 0 6px 8px -1px rgba(168, 251, 202, 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;







